import React from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { buildStructureTimeline } from '../../utils/workoutStructure';
import { formatDistanceKm, formatDurationHMS } from '../../utils/comparison/computeComparisonDeltas';
import {
  multiLapSelectionKey,
  type MultiLapSelection,
} from '../../utils/comparison/lapOverlaySeries';
import {
  lapAvgPower,
  lapAvgHr,
  lapAvgSpeed,
  lapDisplayName,
  lapDistanceM,
  lapDurationSec,
  type LapRow,
} from '../../utils/comparison/lapSegments';
import type { WorkoutDetail } from '../../services/api';
import type { ComparisonMode, SegmentRange } from './types';
import { COMPARISON_COLORS } from './types';

interface ComparisonSegmentTableProps {
  /** When wrapped in an outer panel that already shows the section title. */
  hideTitle?: boolean;
  detailA: WorkoutDetail | null;
  detailB: WorkoutDetail | null;
  mode: ComparisonMode;
  viewMode: 'full' | 'segments';
  segmentA: SegmentRange | null;
  segmentB: SegmentRange | null;
  selectedLapA: number | null;
  selectedLapB: number | null;
  multiSelectedLaps: MultiLapSelection[];
  onSelectLap: (slot: 'A' | 'B', lapIndex: number, source: 'A' | 'B') => void;
  onToggleMultiLap: (source: 'A' | 'B', lapIndex: number) => void;
  onClearMultiLaps: () => void;
}

function fmtLapTime(sec: number | null): string {
  return sec != null && sec > 0 ? formatDurationHMS(sec) : '—';
}

function fmtLapDist(m: number | null): string {
  if (m == null || m <= 0) return '—';
  return m >= 1000 ? formatDistanceKm(m) : `${Math.round(m)} m`;
}

function fmtNum(v: number | null, decimals = 0, unit = ''): string {
  if (v == null) return '—';
  const val = decimals > 0 ? v.toFixed(decimals) : String(Math.round(v));
  return unit ? `${val} ${unit}` : val;
}

function fmtDelta(a: number | null, b: number | null, decimals = 0, unit = ''): string {
  if (a == null || b == null) return '—';
  const d = a - b;
  const sign = d >= 0 ? '+' : '';
  const val = decimals > 0 ? d.toFixed(decimals) : String(Math.round(d));
  return `${sign}${val}${unit ? ` ${unit}` : ''}`;
}

function isMultiSelected(multi: MultiLapSelection[], source: 'A' | 'B', index: number): boolean {
  return multi.some(s => s.source === source && s.index === index);
}

function LapCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-accent)' }}
      aria-label={checked ? 'Deselect lap' : 'Select lap for overlay'}
    />
  );
}

function LapPickerTable({
  title,
  laps,
  source,
  selectedA,
  selectedB,
  allowBothSlots,
  multiSelectedLaps,
  onSelect,
  onToggleMultiLap,
}: {
  title: string;
  laps: LapRow[];
  source: 'A' | 'B';
  selectedA: number | null;
  selectedB: number | null;
  allowBothSlots: boolean;
  multiSelectedLaps: MultiLapSelection[];
  onSelect: (slot: 'A' | 'B', index: number) => void;
  onToggleMultiLap: (source: 'A' | 'B', index: number) => void;
}) {
  if (!laps.length) return null;

  return (
    <div style={{ flex: 1, minWidth: 280 }}>
      {title ? (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-2)', marginBottom: 8 }}>{title}</div>
      ) : null}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--fg-3)', textAlign: 'left' }}>
            <th style={th}>Overlay</th>
            <th style={th}>Lap</th>
            <th style={th}>Time</th>
            <th style={th}>Dist</th>
            <th style={th}>Power</th>
            <th style={th}>HR</th>
            <th style={th}>Segment A</th>
            {allowBothSlots && <th style={th}>Segment B</th>}
          </tr>
        </thead>
        <tbody>
          {laps.map((lap, i) => {
            const isA = selectedA === i;
            const isB = selectedB === i;
            const overlay = isMultiSelected(multiSelectedLaps, source, i);
            return (
              <tr
                key={i}
                style={{ background: isA || isB || overlay ? 'var(--surface-sunken)' : undefined }}
              >
                <td style={td}>
                  <LapCheckbox checked={overlay} onChange={() => onToggleMultiLap(source, i)} />
                </td>
                <td style={td}>{lapDisplayName(lap, i)}</td>
                <td style={td}>{fmtLapTime(lapDurationSec(lap))}</td>
                <td style={td}>{fmtLapDist(lapDistanceM(lap))}</td>
                <td style={td}>{fmtNum(lapAvgPower(lap), 0, 'W')}</td>
                <td style={td}>{fmtNum(lapAvgHr(lap), 0, 'bpm')}</td>
                <td style={td}>
                  <NmButton variant={isA ? 'primary' : 'ghost'} size="sm" onClick={() => onSelect('A', i)}>
                    {isA ? 'Selected' : 'Use A'}
                  </NmButton>
                </td>
                {allowBothSlots && (
                  <td style={td}>
                    <NmButton variant={isB ? 'primary' : 'ghost'} size="sm" onClick={() => onSelect('B', i)}>
                      {isB ? 'Selected' : 'Use B'}
                    </NmButton>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LapSideBySideTable({
  lapsA,
  lapsB,
  mode,
  multiSelectedLaps,
  onToggleMultiLap,
}: {
  lapsA: LapRow[];
  lapsB: LapRow[];
  mode: ComparisonMode;
  multiSelectedLaps: MultiLapSelection[];
  onToggleMultiLap: (source: 'A' | 'B', index: number) => void;
}) {
  const rowCount = Math.max(lapsA.length, lapsB.length);
  if (rowCount === 0) return null;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 680 }}>
        <thead>
          <tr style={{ color: 'var(--fg-3)', textAlign: 'left' }}>
            <th style={th}>A</th>
            <th style={th}>B</th>
            <th style={th}>Lap</th>
            <th style={th}>Time A</th>
            <th style={th}>Time B</th>
            <th style={th}>Δ Time</th>
            <th style={th}>Power A</th>
            <th style={th}>Power B</th>
            <th style={th}>Δ Power</th>
            <th style={th}>HR A</th>
            <th style={th}>HR B</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => {
            const la = lapsA[i];
            const lb = lapsB[i] ?? (mode === 'one-workout-segments' ? lapsA[i] : undefined);
            if (!la && !lb) return null;

            const overlayA = la != null && isMultiSelected(multiSelectedLaps, 'A', i);
            const overlayB = lb != null && isMultiSelected(multiSelectedLaps, 'B', i);
            const timeA = lapDurationSec(la ?? {});
            const timeB = lapDurationSec(lb ?? {});
            const pwrA = lapAvgPower(la ?? {});
            const pwrB = lapAvgPower(lb ?? {});
            const hrA = lapAvgHr(la ?? {});
            const hrB = lapAvgHr(lb ?? {});

            return (
              <tr key={i} style={{ background: overlayA || overlayB ? 'var(--surface-sunken)' : undefined }}>
                <td style={td}>
                  {la ? (
                    <LapCheckbox checked={overlayA} onChange={() => onToggleMultiLap('A', i)} />
                  ) : (
                    '—'
                  )}
                </td>
                <td style={td}>
                  {lb && mode === 'two-workouts' ? (
                    <LapCheckbox checked={overlayB} onChange={() => onToggleMultiLap('B', i)} />
                  ) : (
                    '—'
                  )}
                </td>
                <td style={td}>{lapDisplayName(la ?? lb ?? {}, i)}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.a }}>{fmtLapTime(timeA)}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.b }}>{fmtLapTime(timeB)}</td>
                <td style={td}>{timeA != null && timeB != null ? fmtDelta(timeA, timeB) : '—'}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.a }}>{fmtNum(pwrA, 0, 'W')}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.b }}>{fmtNum(pwrB, 0, 'W')}</td>
                <td style={td}>{fmtDelta(pwrA, pwrB, 0, 'W')}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.a }}>{fmtNum(hrA, 0)}</td>
                <td style={{ ...td, color: COMPARISON_COLORS.b }}>{fmtNum(hrB, 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Metrics table for multi-selected laps only. */
function SelectedLapsMetricsTable({
  multiSelectedLaps,
  lapsA,
  lapsB,
}: {
  multiSelectedLaps: MultiLapSelection[];
  lapsA: LapRow[];
  lapsB: LapRow[];
}) {
  if (multiSelectedLaps.length < 2) return null;

  const rows = multiSelectedLaps.map(sel => {
    const laps = sel.source === 'A' ? lapsA : lapsB;
    const lap = laps[sel.index];
    if (!lap) return null;
    return {
      key: multiLapSelectionKey(sel),
      label: `${sel.source} · ${lapDisplayName(lap, sel.index)}`,
      color: sel.source === 'A' ? COMPARISON_COLORS.a : COMPARISON_COLORS.b,
      time: lapDurationSec(lap),
      dist: lapDistanceM(lap),
      power: lapAvgPower(lap),
      hr: lapAvgHr(lap),
      speed: lapAvgSpeed(lap),
    };
  }).filter((r): r is NonNullable<typeof r> => r != null);

  if (rows.length < 2) return null;

  const bestPower = Math.max(...rows.map(r => r.power ?? -1));

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--surface-sunken)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-2)', marginBottom: 8 }}>
        Selected laps ({rows.length})
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--fg-3)', textAlign: 'left' }}>
            <th style={th}>Lap</th>
            <th style={th}>Time</th>
            <th style={th}>Distance</th>
            <th style={th}>Power</th>
            <th style={th}>HR</th>
            <th style={th}>Speed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key}>
              <td style={{ ...td, color: r.color, fontWeight: 700 }}>{r.label}</td>
              <td style={td}>{fmtLapTime(r.time)}</td>
              <td style={td}>{fmtLapDist(r.dist)}</td>
              <td style={{ ...td, fontWeight: r.power === bestPower ? 800 : 400 }}>
                {fmtNum(r.power, 0, 'W')}
                {r.power === bestPower && r.power != null ? ' ★' : ''}
              </td>
              <td style={td}>{fmtNum(r.hr, 0, 'bpm')}</td>
              <td style={td}>{fmtNum(r.speed, 1, 'km/h')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ComparisonSegmentTable({
  hideTitle = false,
  detailA,
  detailB,
  mode,
  viewMode,
  segmentA,
  segmentB,
  selectedLapA,
  selectedLapB,
  multiSelectedLaps,
  onSelectLap,
  onToggleMultiLap,
  onClearMultiLaps,
}: ComparisonSegmentTableProps) {
  const { t } = useTranslation();
  const lapsA = (detailA?.details?.lap_data ?? []) as LapRow[];
  const lapsB = (detailB?.details?.lap_data ?? []) as LapRow[];
  const hasLaps = lapsA.length > 0 || lapsB.length > 0;

  const structureRows =
    !hasLaps && detailA?.structure
      ? buildStructureTimeline(detailA.structure, detailA.total_time ?? 0).slice(0, 8)
      : [];

  if (!hasLaps && !structureRows.length) return null;

  const showLapPicker = viewMode === 'segments' && hasLaps;

  return (
    <div
      style={{
        marginTop: 12,
        background: 'var(--surface-0)',
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--nm-raised-sm)',
        padding: 14,
      }}
    >
      {(hideTitle ? hasLaps || multiSelectedLaps.length > 0 : true) && (
        <div
          style={{
            display: 'flex',
            justifyContent: hideTitle ? 'flex-end' : 'space-between',
            alignItems: 'center',
            marginBottom: 10,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {!hideTitle && (
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 800 }}>
              {hasLaps ? t('compare.segmentTable.lapComparison') : t('compare.segmentTable.segmentsInComparison')}
            </h4>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {hasLaps && (
              <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {t('compare.segmentTable.overlayHint')}
                {multiSelectedLaps.length > 0 ? t('compare.segmentTable.selectedCount', { count: multiSelectedLaps.length }) : ''}
              </span>
            )}
            {multiSelectedLaps.length > 0 && (
              <NmButton variant="ghost" size="sm" onClick={onClearMultiLaps}>
                {t('compare.segmentTable.clearSelection')}
              </NmButton>
            )}
          </div>
        </div>
      )}

      {showLapPicker ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {mode === 'one-workout-segments' ? (
            <LapPickerTable
              title={t('compare.lapTitle')}
              laps={lapsA}
              source="A"
              selectedA={selectedLapA}
              selectedB={selectedLapB}
              allowBothSlots
              multiSelectedLaps={multiSelectedLaps}
              onSelect={(slot, index) => onSelectLap(slot, index, 'A')}
              onToggleMultiLap={onToggleMultiLap}
            />
          ) : (
            <>
              <LapPickerTable
                title="Workout A laps"
                laps={lapsA}
                source="A"
                selectedA={selectedLapA}
                selectedB={null}
                allowBothSlots={false}
                multiSelectedLaps={multiSelectedLaps}
                onSelect={(slot, index) => onSelectLap(slot, index, 'A')}
                onToggleMultiLap={onToggleMultiLap}
              />
              <LapPickerTable
                title="Workout B laps"
                laps={lapsB}
                source="B"
                selectedA={selectedLapB}
                selectedB={null}
                allowBothSlots={false}
                multiSelectedLaps={multiSelectedLaps}
                onSelect={(_, index) => onSelectLap('B', index, 'B')}
                onToggleMultiLap={onToggleMultiLap}
              />
            </>
          )}
        </div>
      ) : hasLaps ? (
        <LapSideBySideTable
          lapsA={lapsA}
          lapsB={lapsB}
          mode={mode}
          multiSelectedLaps={multiSelectedLaps}
          onToggleMultiLap={onToggleMultiLap}
        />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: 'var(--fg-3)', textAlign: 'left' }}>
              <th style={th}>Segment</th>
              <th style={th}>A</th>
              <th style={th}>B</th>
            </tr>
          </thead>
          <tbody>
            {structureRows.map((seg, i) => (
              <tr key={i}>
                <td style={td}>{seg.name ?? `Segment ${i + 1}`}</td>
                <td style={td}>{seg.durationSec != null ? `${Math.round(seg.durationSec / 60)}m` : '—'}</td>
                <td style={td}>—</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <SelectedLapsMetricsTable
        multiSelectedLaps={multiSelectedLaps}
        lapsA={lapsA}
        lapsB={lapsB}
      />

      {showLapPicker && segmentA && segmentB && (
        <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--fg-3)' }}>
          <span style={{ color: COMPARISON_COLORS.a }}>A</span> {formatDurationHMS(segmentA.startSec)}–
          {formatDurationHMS(segmentA.endSec)}
          {' · '}
          <span style={{ color: COMPARISON_COLORS.b }}>B</span> {formatDurationHMS(segmentB.startSec)}–
          {formatDurationHMS(segmentB.endSec)}
        </p>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--surface-sunken)' };
const td: React.CSSProperties = { padding: '8px', borderBottom: '1px solid var(--surface-sunken)' };
